/**
 * 候选人简历筛选详情 —— 深度人岗匹配诊断报告。
 *
 * 用在 AdminJobDetail.tsx 的简历筛选行下,点候选人展开后展示。
 */

import type { ScreeningResult } from '../api/types';

export function ScreeningDetail({ s }: { s: ScreeningResult }) {
  const persona = s.persona_fit;
  const dims = s.capability_dimension_scores ?? [];
  const mustChecks = s.must_have_check ?? [];
  const advs = s.advantage_signals ?? [];
  const cons = s.concern_signals ?? [];
  const focus = s.interview_focus_questions ?? [];
  const recDetail = s.recommendation_detail;

  return (
    <div style={{
      padding: 14,
      background: 'var(--bg-elev)',
      borderRadius: 8,
      marginTop: 8,
      fontSize: 13,
    }}>
      {/* 候选人核心定位 */}
      {s.candidate_summary && (
        <div style={{
          padding: 10,
          background: 'var(--accent-soft)',
          borderLeft: '3px solid var(--accent)',
          borderRadius: 4,
          marginBottom: 12,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 4 }}>📌 候选人定位</div>
          <div style={{ lineHeight: 1.6 }}>{s.candidate_summary}</div>
        </div>
      )}

      {/* persona_fit */}
      {persona && (
        <Block title="① 候选人画像匹配">
          <KV k="匹配类型" v={`${persona.matched_category} (${strengthLabel(persona.match_strength)})`} highlight />
          {persona.matched_evidence && <KV k="支撑证据" v={persona.matched_evidence} muted />}
          <KV k="工作年限" v={persona.experience_years_fit} />
          <KV k="地理因素" v={persona.geographic_fit} />
          {persona.exclude_hits && persona.exclude_hits.length > 0 && (
            <div style={{ marginTop: 6, padding: 8, background: 'var(--danger-soft)', borderRadius: 4 }}>
              <div style={{ fontWeight: 600, color: '#b91c1c', fontSize: 12, marginBottom: 4 }}>⚠️ 命中应排除类型</div>
              {persona.exclude_hits.map((ex, i) => (
                <div key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                  <strong>× {ex.type}</strong> — {ex.evidence}
                </div>
              ))}
            </div>
          )}
        </Block>
      )}

      {/* 能力 6 维加权评分 */}
      {dims.length > 0 && (
        <Block title={`② 能力 ${dims.length} 维评分${s.weighted_overall_score ? ` (加权总分 ${s.weighted_overall_score})` : ''}`}>
          <div className="col" style={{ gap: 6 }}>
            {dims.map((d, i) => (
              <div key={i} style={{
                padding: '6px 10px',
                background: 'var(--surface)',
                borderRadius: 4,
                borderLeft: `3px solid ${scoreColor(d.score)}`,
              }}>
                <div className="row" style={{ alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, minWidth: 90 }}>{d.dimension}</span>
                  <span className="hint" style={{ fontSize: 11 }}>权重 {d.weight_pct}%</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: scoreColor(d.score) }}>
                    {d.score}/10
                  </span>
                </div>
                {d.evidence && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.evidence}</div>}
              </div>
            ))}
          </div>
        </Block>
      )}

      {/* must_have 核查 */}
      {mustChecks.length > 0 && (
        <Block title="③ 必备能力核查">
          <div className="col" style={{ gap: 4 }}>
            {mustChecks.map((m, i) => (
              <div key={i} style={{
                padding: '4px 10px',
                fontSize: 12,
                borderLeft: `2px solid ${satColor(m.satisfied)}`,
                paddingLeft: 8,
              }}>
                <span style={{ fontWeight: 600 }}>{satEmoji(m.satisfied)} {m.capability}</span>
                {m.evidence && <span className="hint" style={{ marginLeft: 6 }}>— {m.evidence}</span>}
              </div>
            ))}
          </div>
        </Block>
      )}

      {/* 亮点 + 疑点 */}
      {(advs.length > 0 || cons.length > 0) && (
        <div className="row" style={{ gap: 12, alignItems: 'stretch', marginTop: 12 }}>
          {advs.length > 0 && (
            <div style={{ flex: 1, padding: 10, background: 'var(--success-soft)', borderRadius: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#047857', marginBottom: 6 }}>✓ 亮点</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}>
                {advs.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          {cons.length > 0 && (
            <div style={{ flex: 1, padding: 10, background: 'var(--warn-soft)', borderRadius: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#b45309', marginBottom: 6 }}>? 疑点</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}>
                {cons.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* 面试追问建议 */}
      {focus.length > 0 && (
        <Block title="④ 面试时重点追问">
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12, lineHeight: 1.7 }}>
            {focus.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </Block>
      )}

      {/* 战略级判断 */}
      {recDetail && (
        <div style={{
          padding: 10,
          background: 'var(--info-soft)',
          borderLeft: '3px solid var(--info)',
          borderRadius: 4,
          marginTop: 12,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#4338ca', marginBottom: 4 }}>
            🎯 战略级判断:{decisionLabel(recDetail.decision)}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>{recDetail.one_line_reasoning}</div>
        </div>
      )}
    </div>
  );
}


// ───── 小组件 ─────

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function KV({ k, v, highlight, muted }: { k: string; v?: string | null; highlight?: boolean; muted?: boolean }) {
  if (!v) return null;
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12, padding: '3px 0' }}>
      <span style={{ minWidth: 70, color: 'var(--text-muted)' }}>{k}</span>
      <span style={{
        flex: 1,
        fontWeight: highlight ? 600 : 400,
        color: muted ? 'var(--text-muted)' : (highlight ? 'var(--accent)' : 'var(--text)'),
        fontStyle: muted ? 'italic' : 'normal',
      }}>{v}</span>
    </div>
  );
}

// ───── 工具 ─────

function strengthLabel(s: string): string {
  return ({ strong: '强匹配 ⭐⭐⭐', medium: '中等匹配 ⭐⭐', weak: '弱匹配 ⭐', none: '无明确匹配' } as Record<string, string>)[s] || s;
}
function decisionLabel(d: string): string {
  return ({ strong_yes: '强烈推荐', yes: '推荐', maybe: '可考虑', no: '不推荐' } as Record<string, string>)[d] || d;
}
function scoreColor(s: number): string {
  if (s >= 7) return 'var(--success)';
  if (s >= 5) return 'var(--warn)';
  return 'var(--danger)';
}
function satColor(s: string): string {
  return ({ yes: 'var(--success)', partial: 'var(--warn)', no: 'var(--danger)' } as Record<string, string>)[s] || 'var(--text-muted)';
}
function satEmoji(s: string): string {
  return ({ yes: '✅', partial: '⚠️', no: '❌' } as Record<string, string>)[s] || '・';
}
